import { useEffect, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { Aabb } from "./WalkableOffice";
import { audio } from "../lib/audio";

type Props = {
  enabled: boolean;
  colliders: Aabb[];
  /** Eye height (metres). */
  eyeHeight?: number;
  /** Spawn position on the floor plane. */
  spawn?: [number, number, number];
  /** World-space point the camera should initially face (from the waypoint). */
  lookTarget?: [number, number, number];
  /** Override the scripted-glide duration (ms) for the NEXT waypoint change
   *  only — e.g. a slow cinematic intro pan vs. the default snappy walk-up
   *  cut. Falls back to `CAM_TWEEN_MS` when unset. */
  tweenMs?: number;
  /** Per-waypoint field of view (degrees) — e.g. a tight push-in on a screen.
   *  Falls back to the Canvas's base fov (see Scene.tsx) when unset. Was
   *  previously only wired for the non-walkable CameraRig/InspectSnap path,
   *  so every walkable waypoint's authored `fov` was silently ignored. */
  fov?: number;
  moveSpeed?: number;
  /**
   * Floor-gated containment: only allow a step when there is real floor mesh
   * directly beneath the candidate position. This contains an irregular
   * multi-room glTF (meeting room / reception / corridor / office) far more
   * reliably than a hand-authored perimeter rectangle — you physically cannot
   * walk off the floored area into the exterior void.
   */
  floorGated?: boolean;
  /** Regex the floor meshes' names match (default /floor|carpet/i). */
  floorMatch?: RegExp;
  /** Loop footsteps while this controller is actually moving. */
  walkSteps?: boolean;
};

/** Yaw (our YXZ convention) that makes the camera look from `from` toward `to`. */
function yawToward(from: [number, number, number], to: [number, number, number]): number {
  const dx = to[0] - from[0];
  const dz = to[2] - from[2];
  if (dx * dx + dz * dz < 1e-6) return 0;
  return Math.atan2(-dx, -dz);
}

/** Pitch (radians, clamped to the same +-1.2 range as interactive look) that
 *  makes the camera look from `from` toward `to`, accounting for the target's
 *  height difference. Previously the walkable rig always snapped pitch to 0
 *  on every waypoint teleport regardless of `lookTarget`'s Y — fine for
 *  purely-horizontal free-roam spawns, but it meant a scripted "push in and
 *  look down/up at something" waypoint (e.g. a close-up on a laptop screen or
 *  a monitor) silently had NO effect: the camera arrived at the right spot
 *  but kept looking dead level. Sign convention matches `camera.rotation.x`:
 *  a positive pitch tilts the view up (see the mouse-look code below, which
 *  already relies on this same convention). */
function pitchToward(from: [number, number, number], to: [number, number, number]): number {
  const dx = to[0] - from[0];
  const dz = to[2] - from[2];
  const dy = to[1] - from[1];
  const horiz = Math.hypot(dx, dz);
  if (horiz < 1e-6 && Math.abs(dy) < 1e-6) return 0;
  return Math.max(-1.2, Math.min(1.2, Math.atan2(dy, horiz)));
}

/** Shortest-path angle interpolation (never spins the long way around). */
function lerpAngle(a: number, b: number, t: number): number {
  let diff = b - a;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return a + diff * t;
}

/** Duration of the scripted glide below — exported so callers (App.tsx) can
 *  size their own "wait for the camera to settle before advancing the beat"
 *  timeout to match instead of guessing a separate magic number. */
export const CAM_TWEEN_MS = 900;

const _fwd = new THREE.Vector3();
const _right = new THREE.Vector3();
const _wish = new THREE.Vector3();
const _next = new THREE.Vector3();

function overlapsXZ(x: number, z: number, r: number, box: Aabb): boolean {
  const nx = Math.max(box.min[0], Math.min(x, box.max[0]));
  const nz = Math.max(box.min[2], Math.min(z, box.max[2]));
  const dx = x - nx;
  const dz = z - nz;
  return dx * dx + dz * dz < r * r;
}

function resolveMove(
  from: THREE.Vector3,
  dx: number,
  dz: number,
  radius: number,
  colliders: Aabb[],
  floorAt: ((x: number, z: number) => boolean) | null,
): { x: number; z: number } {
  let x = from.x + dx;
  let z = from.z + dz;
  // Separate axes so you slide along walls.
  if (colliders.some((c) => overlapsXZ(x, from.z, radius, c))) x = from.x;
  if (colliders.some((c) => overlapsXZ(x, z, radius, c))) z = from.z;
  // Floor gating: a candidate step is only allowed if there is real floor under
  // it. Test each axis independently so you still slide along the floor edge
  // instead of sticking when one axis would step into the void.
  if (floorAt) {
    if (x !== from.x && !floorAt(x, from.z)) x = from.x;
    if (z !== from.z && !floorAt(x, z)) z = from.z;
  }
  return { x, z };
}

/**
 * Click-to-capture pointer lock, WASD walk, mouse look, AABB collision.
 * Used for photoreal Marble splat walk and (legacy) mesh rooms.
 */
export function FirstPersonControls({
  enabled,
  colliders,
  eyeHeight = 1.65,
  spawn = [0, 0, 2.2],
  lookTarget,
  tweenMs,
  fov,
  moveSpeed = 3.2,
  floorGated = false,
  floorMatch = /floor|carpet/i,
  walkSteps = false,
}: Props) {
  const { camera, gl, scene } = useThree();
  // Whatever fov the Canvas was created with (Scene.tsx's `camera={{ fov: ... }}`)
  // — the fallback whenever a waypoint doesn't author its own `fov`, so a
  // close-up push-in never "sticks" zoomed on a later waypoint that never
  // asked for it.
  const baseFov = useRef(camera instanceof THREE.PerspectiveCamera ? camera.fov : 50).current;
  const keys = useRef<Record<string, boolean>>({});
  const yaw = useRef(0);
  const pitch = useRef(0);
  const locked = useRef(false);
  const pos = useRef(new THREE.Vector3(spawn[0], eyeHeight, spawn[2]));
  // Floor meshes (collected once) + a down-ray to test "is there floor here?".
  const floorMeshes = useRef<THREE.Mesh[]>([]);
  const ray = useRef(new THREE.Raycaster());
  const rayOrigin = useRef(new THREE.Vector3());
  const DOWN = useRef(new THREE.Vector3(0, -1, 0)).current;
  const mountedOnce = useRef(false);
  const tween = useRef<{
    fromPos: THREE.Vector3;
    toPos: THREE.Vector3;
    fromYaw: number;
    toYaw: number;
    fromPitch: number;
    toPitch: number;
    fromFov: number;
    toFov: number;
    t0: number;
    durationMs: number;
    /** cinematic (custom tweenMs, e.g. a scripted intro pan) tweens ease IN
     *  and out — the default snappy camera-cut keeps the original ease-OUT
     *  (max velocity at the start) since a 900ms cut is meant to feel snappy,
     *  not glide. A cinematic pan easing out-only reads as a "quick snap then
     *  crawl" no matter how long its duration is, because velocity peaks at
     *  t=0 regardless of total length. */
    easeInOut: boolean;
  } | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const toYaw = lookTarget ? yawToward([spawn[0], eyeHeight, spawn[2]], lookTarget) : yaw.current;
    const toPitch = lookTarget ? pitchToward([spawn[0], eyeHeight, spawn[2]], lookTarget) : 0;
    if (!mountedOnce.current) {
      // First mount: snap instantly (there is no "current" pose to glide
      // from yet — react-three-fiber's <Canvas camera> prop already placed
      // the camera here for frame 1).
      mountedOnce.current = true;
      pos.current.set(spawn[0], eyeHeight, spawn[2]);
      yaw.current = toYaw;
      pitch.current = toPitch;
      camera.position.copy(pos.current);
      camera.rotation.order = "YXZ";
      camera.rotation.y = yaw.current;
      camera.rotation.x = pitch.current;
      camera.rotation.z = 0;
      if (camera instanceof THREE.PerspectiveCamera) {
        camera.fov = fov ?? baseFov;
        camera.updateProjectionMatrix();
      }
      return;
    }
    // A scripted waypoint change (story advanced to a new "camera" beat, or
    // free-roam just started) — glide there instead of an instant teleport,
    // so e.g. "walk up to the counter" or "push in on the laptop" visually
    // reads as movement. Continuous WASD walking never re-triggers this
    // effect (spawn/lookTarget only change when the beat graph advances to a
    // NEW waypoint), so this can never fight the player's own input.
    tween.current = {
      fromPos: pos.current.clone(),
      toPos: new THREE.Vector3(spawn[0], eyeHeight, spawn[2]),
      fromYaw: yaw.current,
      toYaw,
      fromPitch: pitch.current,
      toPitch,
      fromFov: camera instanceof THREE.PerspectiveCamera ? camera.fov : baseFov,
      toFov: fov ?? baseFov,
      t0: performance.now(),
      durationMs: tweenMs && tweenMs > 0 ? tweenMs : CAM_TWEEN_MS,
      easeInOut: !!(tweenMs && tweenMs > 0),
    };
  }, [
    enabled,
    spawn[0],
    spawn[1],
    spawn[2],
    lookTarget?.[0],
    lookTarget?.[1],
    lookTarget?.[2],
    eyeHeight,
    tweenMs,
    fov,
    baseFov,
    camera,
  ]);

  // After Marble autoFit, snap into the suggested establish pose (avoids spawning
  // in the black void outside a mis-scaled splat).
  useEffect(() => {
    if (!enabled) return;
    let tries = 0;
    const id = window.setInterval(() => {
      tries += 1;
      const fit = (window as unknown as {
        __splatFit?: { suggestEstablish?: { position: number[]; target: number[] } };
      }).__splatFit;
      const est = fit?.suggestEstablish;
      if (!est?.position) {
        if (tries > 80) window.clearInterval(id);
        return;
      }
      window.clearInterval(id);
      const [x, y, z] = est.position;
      pos.current.set(x, y || eyeHeight, z);
      const tx = est.target?.[0] ?? 0;
      const tz = est.target?.[2] ?? z - 1;
      yaw.current = Math.atan2(-(tx - x), -(tz - z));
      pitch.current = 0;
      camera.position.copy(pos.current);
      camera.rotation.order = "YXZ";
      camera.rotation.y = yaw.current;
      camera.rotation.x = pitch.current;
    camera.rotation.z = 0;
    }, 250);
    return () => window.clearInterval(id);
  }, [enabled, eyeHeight, camera]);

  // Collect floor meshes once the glTF is on the scene (poll until ready).
  useEffect(() => {
    if (!enabled || !floorGated) return;
    let tries = 0;
    const collect = () => {
      const found: THREE.Mesh[] = [];
      scene.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh && floorMatch.test(m.name || "")) found.push(m);
      });
      if (found.length > 0) {
        floorMeshes.current = found;
        return true;
      }
      return false;
    };
    if (collect()) return;
    const id = window.setInterval(() => {
      tries += 1;
      if (collect() || tries > 120) window.clearInterval(id);
    }, 200);
    return () => window.clearInterval(id);
  }, [enabled, floorGated, scene, floorMatch]);

  useEffect(() => {
    if (!enabled) return;
    const el = gl.domElement;
    el.style.touchAction = "none";

    const onKey = (e: KeyboardEvent, down: boolean) => {
      // Never steal keys while the learner is typing (e.g. the PC login screen —
      // preventDefault on KeyW/KeyA/KeyS/KeyD would swallow those letters) or
      // while a 2D overlay owns the screen.
      if (down) {
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
        if (document.querySelector(".cf-pc-overlay, .privhunt-card, .privlearn-list, .th-root, .arc-root")) return;
      }
      keys.current[e.code] = down;
      if (["KeyW", "KeyA", "KeyS", "KeyD", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.code)) {
        e.preventDefault();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => onKey(e, true);
    const onKeyUp = (e: KeyboardEvent) => onKey(e, false);

    // Drag-to-look WITHOUT pointer lock (mouse drag or touch), so the on-screen
    // HUD and clickable room hotspots stay usable. A plain left-click in the
    // room NO LONGER auto-captures the mouse — that was confusing (a click meant
    // to inspect an item or just look around would silently lock the cursor and
    // force a reticle). Mouse capture is now opt-in ONLY via the explicit
    // "Capture mouse" HUD button; WASD, drag-to-look and the on-screen pad all
    // work fully un-captured.
    let dragActive = false;
    let lastX = 0;
    let lastY = 0;
    const onPointerDown = (e: PointerEvent) => {
      if (locked.current || e.button !== 0) return;
      dragActive = true;
      lastX = e.clientX;
      lastY = e.clientY;
    };
    const onPointerMove = (e: PointerEvent) => {
      if (locked.current || !dragActive) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      yaw.current -= dx * 0.004;
      pitch.current = Math.max(-1.2, Math.min(1.2, pitch.current - dy * 0.004));
    };
    const onPointerUp = () => {
      dragActive = false;
    };

    const onLockChange = () => {
      locked.current = document.pointerLockElement === el;
      (window as unknown as { __fpLocked?: boolean }).__fpLocked = locked.current;
    };
    const onMouse = (e: MouseEvent) => {
      if (!locked.current) return;
      yaw.current -= e.movementX * 0.0022;
      pitch.current -= e.movementY * 0.0022;
      pitch.current = Math.max(-1.2, Math.min(1.2, pitch.current));
    };

    // On-screen HUD channel (WalkControls): move like held keys, turn in steps.
    const onHudControl = (e: Event) => {
      const d = (e as CustomEvent).detail as
        | { kind: "move"; dir: "fwd" | "back" | "left" | "right"; down: boolean }
        | { kind: "turn"; dx?: number; dy?: number }
        | { kind: "stopall" }
        | undefined;
      if (!d) return;
      if (d.kind === "move") {
        const code = { fwd: "KeyW", back: "KeyS", left: "KeyA", right: "KeyD" }[d.dir];
        if (code) keys.current[code] = d.down;
      } else if (d.kind === "turn") {
        yaw.current -= d.dx ?? 0;
        pitch.current = Math.max(-1.2, Math.min(1.2, pitch.current - (d.dy ?? 0)));
      } else if (d.kind === "stopall") {
        keys.current = {};
      }
    };

    // QA-only teleport hook (harmless in prod; mirrors ?beat= dev hooks). Lets
    // headless QA jump to a spot + heading without a slow software-GL walk.
    (window as unknown as {
      __fpTeleport?: (x: number, z: number, yawRad: number) => void;
    }).__fpTeleport = (x: number, z: number, yawRad: number) => {
      pos.current.set(x, eyeHeight, z);
      yaw.current = yawRad;
      pitch.current = 0;
    };

    el.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointerlockchange", onLockChange);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("keyup", onKeyUp);
    document.addEventListener("mousemove", onMouse);
    window.addEventListener("cf-fp-control", onHudControl);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointerlockchange", onLockChange);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("keyup", onKeyUp);
      document.removeEventListener("mousemove", onMouse);
      window.removeEventListener("cf-fp-control", onHudControl);
      if (document.pointerLockElement === el) document.exitPointerLock();
    };
  }, [enabled, gl]);

  useFrame((_, dt) => {
    if (!enabled) return;
    if (tween.current) {
      const tw = tween.current;
      const elapsed = performance.now() - tw.t0;
      const raw = Math.min(1, elapsed / tw.durationMs);
      const eased = tw.easeInOut
        ? raw < 0.5
          ? 4 * raw * raw * raw
          : 1 - Math.pow(-2 * raw + 2, 3) / 2 // ease-in-out cubic
        : 1 - Math.pow(1 - raw, 3); // ease-out cubic
      pos.current.lerpVectors(tw.fromPos, tw.toPos, eased);
      yaw.current = lerpAngle(tw.fromYaw, tw.toYaw, eased);
      pitch.current = tw.fromPitch + (tw.toPitch - tw.fromPitch) * eased;
      camera.rotation.order = "YXZ";
      camera.rotation.y = yaw.current;
      camera.rotation.x = pitch.current;
      camera.rotation.z = 0;
      camera.position.copy(pos.current);
      if (camera instanceof THREE.PerspectiveCamera && tw.fromFov !== tw.toFov) {
        camera.fov = tw.fromFov + (tw.toFov - tw.fromFov) * eased;
        camera.updateProjectionMatrix();
      }
      (window as unknown as { __fpPos?: number[]; __fpLocked?: boolean }).__fpPos = [
        pos.current.x,
        pos.current.y,
        pos.current.z,
      ];
      if (raw >= 1) tween.current = null;
      if (walkSteps) audio.walkLoop(false);
      return; // freeze WASD/look input until the scripted glide settles
    }
    const t = Math.min(dt, 0.05);
    camera.rotation.order = "YXZ";
    camera.rotation.y = yaw.current;
    camera.rotation.x = pitch.current;
    camera.rotation.z = 0;

    // WASD works even before pointer-lock (mouse look still needs a click).
    _fwd.set(-Math.sin(yaw.current), 0, -Math.cos(yaw.current));
    _right.set(Math.cos(yaw.current), 0, -Math.sin(yaw.current));
    _wish.set(0, 0, 0);
    const k = keys.current;
    if (k.KeyW || k.ArrowUp) _wish.add(_fwd);
    if (k.KeyS || k.ArrowDown) _wish.sub(_fwd);
    if (k.KeyD || k.ArrowRight) _wish.add(_right);
    if (k.KeyA || k.ArrowLeft) _wish.sub(_right);
    if (_wish.lengthSq() > 0) {
      _wish.normalize().multiplyScalar(moveSpeed * t);
      const floorAt =
        floorGated && floorMeshes.current.length > 0
          ? (x: number, z: number) => {
              // Cast straight down from just above the eye; accept the step only
              // if it hits floor within reach beneath the feet.
              rayOrigin.current.set(x, eyeHeight + 0.3, z);
              ray.current.set(rayOrigin.current, DOWN);
              ray.current.far = eyeHeight + 1.2;
              const hits = ray.current.intersectObjects(floorMeshes.current, false);
              return hits.length > 0;
            }
          : null;
      const { x, z } = resolveMove(pos.current, _wish.x, _wish.z, 0.28, colliders, floorAt);
      pos.current.x = x;
      pos.current.z = z;
    }
    if (walkSteps) audio.walkLoop(_wish.lengthSq() > 0);
    pos.current.y = eyeHeight;
    camera.position.copy(pos.current);
    _next.copy(pos.current);
    (window as unknown as { __fpPos?: number[]; __fpLocked?: boolean }).__fpPos = [
      pos.current.x,
      pos.current.y,
      pos.current.z,
    ];
  });

  return null;
}
